import { useState, useEffect } from 'react';
import { Truck, Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white shadow-md'
            : 'bg-transparent backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2">
              <Truck
                className={`w-6 h-6 ${scrolled ? 'text-blue-600' : 'text-white'}`}
              />
              <span
                className={`font-bold text-xl ${scrolled ? 'text-gray-900' : 'text-white'}`}
              >
                LoadApp
              </span>
            </a>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-blue-500 ${
                    scrolled ? 'text-gray-700' : 'text-white/90'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Desktop CTA buttons */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="/auth/login"
                className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors duration-200 ${
                  scrolled
                    ? 'border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                    : 'border-white/40 text-white hover:border-white hover:bg-white/10'
                }`}
              >
                Log In
              </a>
              <a
                href="/auth/register"
                className="text-sm font-medium px-4 py-2 rounded-full text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:opacity-90 transition-opacity duration-200"
              >
                Get Started Free
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className={`w-6 h-6 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile full-screen overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex flex-col">
          <div className="flex items-center justify-between px-4 h-16">
            <div className="flex items-center gap-2">
              <Truck className="w-6 h-6 text-white" />
              <span className="font-bold text-xl text-white">LoadApp</span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2"
              aria-label="Close menu"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          <div className="flex flex-col items-center justify-center flex-1 gap-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-2xl font-semibold text-white hover:text-cyan-400 transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}

            <div className="flex flex-col items-center gap-4 mt-4">
              <a
                href="/auth/login"
                className="text-base font-medium px-8 py-3 rounded-full border border-white/40 text-white hover:border-white hover:bg-white/10 transition-colors duration-200"
              >
                Log In
              </a>
              <a
                href="/auth/register"
                className="text-base font-medium px-8 py-3 rounded-full text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:opacity-90 transition-opacity duration-200"
              >
                Get Started Free
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
